import { importItemsActions } from '@common-reducers/ImportItemsReducer'
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { namespace } from '@common/config/constants'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Settings2 } from 'lucide-react'
import { supportedColumnTypes } from './import-excel'
import ManageColumnsForImportExcel from './manage-columns-for-import-excel'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

type Props = {
    importJsonData?: any[];
    columnsList: Array<{ _id?: string; type?: string; title?: string }>;
}

export default function MapImportedDataDialog({
    importJsonData = undefined,
    columnsList,
}: Props) {
    const dispatch = useAppDispatch()
    const { t } = useTranslation(namespace);
    // 'campaign' = the audience import (an audience is a board under the hood).
    const isAudienceImport = useAppSelector((state) => state.ModalReducer.openedby === 'campaign');

    const importDataMapping = useAppSelector((state) => state.ImportItemsReducer.importDataMapping);
    const newColumnList = useAppSelector((state) => state.ImportItemsReducer.newColumnList);
    const [manageColumnsOpen, setManageColumnsOpen] = useState(false);


    const excelColumns = useMemo(() => {
        if (importJsonData) {
            let joinedObject = {};
            for (const row of importJsonData) {
                joinedObject = { ...joinedObject, ...row };
            }
            const _excelColumns = Object.keys(joinedObject)

            return _excelColumns;
        }
        else {
            return ['First name', 'Last name']
        }
    }, [importJsonData])

    const availableColumns = useMemo(() => {
        return [...columnsList, ...(newColumnList || [])]
            .filter(column => supportedColumnTypes.includes(column.type ?? ''));
    }, [columnsList, newColumnList])



    function selectColumnToMap(key: string, _selectedColumn: string) {
        if (_selectedColumn === 'taskName') {
            dispatch(importItemsActions.setSelectedColumn(key))
        }
        dispatch(importItemsActions.setImportDataMapping({
            ...importDataMapping,
            [key]: _selectedColumn
        }))
    }



    return (
        <>
            <div className="flex flex-col h-full space-y-4">
                {/* Header */}
                <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-[var(--color-text)] dark:text-[var(--color-text)]">
                        {/* The audience import maps each column of the uploaded file (CSV/XLSX/XLS)
                            onto a field of the audience — say that, not the board import's
                            "Excel" / "board columns" wording (an audience is a board only under the hood). */}
                        {isAudienceImport
                            ? t('importExcelToBlueticks.mapFileToAudience')
                            : <>{t('importExcelToBlueticks.mapColumnsFromExcel')}{' '}{t('importExcelToBlueticks.toColumnsInBoard')}</>}
                    </h3>
                    {/* Not a checkbox: the parser always reads the first row as the column
                        headers (they name the rows below) and never imports it, so a box to
                        "exclude" it was wired to nothing. State the rule instead. */}
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        {t('importExcelToBlueticks.excludeFirstRow')}
                    </p>
                </div>

                {/* Column Mapping Card */}
                <Card className="flex-1 flex flex-col min-h-0 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-3 border-b border-slate-200 dark:border-slate-700">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                                    {isAudienceImport ? t('importExcelToBlueticks.fileBadge') : 'Excel'}
                                </Badge>
                                <span className="text-sm font-semibold text-[var(--color-text)]">
                                    {isAudienceImport ? t('importExcelToBlueticks.fileColumns') : t('importExcelToBlueticks.excelColumns')}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                                    {isAudienceImport ? t('importExcelToBlueticks.audienceBadge') : 'Board'}
                                </Badge>
                                <span className="text-sm font-semibold text-[var(--color-text)]">
                                    {isAudienceImport ? t('importExcelToBlueticks.audienceFields') : t('importExcelToBlueticks.existingBoardColumns')}
                                </span>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 min-h-0 p-0">
                        <ScrollArea className="h-full">
                            <div className="p-4 space-y-2">
                                {excelColumns.map((col: string, index: number) => (
                                    <div
                                        key={index}
                                        className="grid grid-cols-2 gap-4 items-center p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 hover:bg-slate-100/50 dark:hover:bg-slate-800/80 transition-colors"
                                    >
                                        {/* Excel Column */}
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-sm font-medium text-[var(--color-text)] truncate">
                                                {col}
                                            </span>
                                            <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                        </div>

                                        {/* Board Column Select */}
                                        <Select
                                            value={importDataMapping[col] || 'unmapped'}
                                            onValueChange={(value) => selectColumnToMap(col, value === 'unmapped' ? '' : value)}
                                        >
                                            <SelectTrigger className="w-full bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600">
                                                <SelectValue placeholder="Select column..." />
                                            </SelectTrigger>
                                            <SelectContent zIndex={30000}>
                                                <SelectItem value="unmapped">
                                                    None
                                                </SelectItem>
                                                {availableColumns.map((column) => {
                                                    const isMapped = Object.values(importDataMapping).includes(column._id ?? '');
                                                    const displayText = `${column.title ?? column.type}${isMapped ? ' (Mapped)' : ''}`;

                                                    return (
                                                        <SelectItem
                                                            key={column._id || `${column.type}-${column.title}`}
                                                            value={column?._id ?? ''}
                                                            disabled={isMapped}
                                                        >
                                                            {displayText}
                                                        </SelectItem>
                                                    );
                                                })}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Manage Columns Button */}
                <div className="flex justify-end pt-4">
                    <Button
                        variant="outline"
                        onClick={() => setManageColumnsOpen(true)}
                        className="gap-2"
                    >
                        <Settings2 className="w-4 h-4" />
                        Manage Columns
                    </Button>
                </div>
            </div>

            <ManageColumnsForImportExcel
                open={manageColumnsOpen}
                onClose={() => setManageColumnsOpen(false)}
                supportedColumns={columnsList
                    .filter(column => (supportedColumnTypes).includes(column.type ?? '') && column.type !== 'task name')} />
        </>
    )
}
